// Gestione filiali della propria azienda
import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Plus, Pencil, Trash2, MapPin, Phone, Mail, Loader2, CheckCircle2 } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { EmptyState } from '../../components/ui/EmptyState'
import { CardSkeleton } from '../../components/ui/Skeleton'
import api from '../../lib/api'

async function geocodeAddress(address: string, city: string, postalCode: string): Promise<{ lat: string; lng: string } | null> {
  const parts = [address, postalCode, city, 'Italia'].filter(Boolean).join(', ')
  if (!parts.trim() || (!address && !city)) return null
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(parts)}`
    const res = await fetch(url, { headers: { 'Accept-Language': 'it', 'User-Agent': 'FieldCore/1.0' } })
    const data = await res.json() as Array<{ lat: string; lon: string }>
    if (data.length > 0 && data[0]) {
      return { lat: parseFloat(data[0].lat).toFixed(6), lng: parseFloat(data[0].lon).toFixed(6) }
    }
  } catch { /* silenzioso */ }
  return null
}

interface Branch {
  id: string
  name: string
  address?: string
  city?: string
  postalCode?: string
  phone?: string
  email?: string
  notes?: string // JSON with {lat, lng}
}

interface BranchFormProps {
  initial?: Branch
  onSave: (data: Partial<Branch> & { lat?: string; lng?: string }) => void
  onCancel: () => void
  loading: boolean
}

function BranchForm({ initial, onSave, onCancel, loading }: BranchFormProps) {
  const parseGeo = (notes?: string) => {
    try { return notes ? (JSON.parse(notes) as { lat?: number; lng?: number }) : {} } catch { return {} }
  }
  const geo = parseGeo(initial?.notes)

  const [form, setForm] = useState({
    name: initial?.name ?? '',
    address: initial?.address ?? '',
    city: initial?.city ?? '',
    postalCode: initial?.postalCode ?? '',
    phone: initial?.phone ?? '',
    email: initial?.email ?? '',
    lat: geo.lat?.toString() ?? '',
    lng: geo.lng?.toString() ?? '',
  })
  const [geocoding, setGeocoding] = useState(false)
  const [geocoded, setGeocoded] = useState(false)

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(p => ({ ...p, [k]: e.target.value }))
    if (['address', 'city', 'postalCode'].includes(k)) setGeocoded(false)
  }

  const runGeocode = useCallback(async () => {
    const { address, city, postalCode } = form
    if (!address && !city) return
    setGeocoding(true)
    setGeocoded(false)
    const result = await geocodeAddress(address, city, postalCode)
    setGeocoding(false)
    if (result) {
      setForm(p => ({ ...p, lat: result.lat, lng: result.lng }))
      setGeocoded(true)
    }
  }, [form])

  const handleAddressBlur = () => { void runGeocode() }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label="Nome filiale *" value={form.name} onChange={set('name')} placeholder="Filiale Nord" className="sm:col-span-2" />
        <Input
          label="Indirizzo"
          value={form.address}
          onChange={set('address')}
          onBlur={handleAddressBlur}
          placeholder="Via Roma 1"
          className="sm:col-span-2"
        />
        <Input label="Città" value={form.city} onChange={set('city')} onBlur={handleAddressBlur} placeholder="Milano" />
        <Input label="CAP" value={form.postalCode} onChange={set('postalCode')} onBlur={handleAddressBlur} placeholder="20121" />
        <Input label="Telefono" value={form.phone} onChange={set('phone')} placeholder="+39 02 1234567" />
        <Input label="Email" type="email" value={form.email} onChange={set('email')} placeholder="filiale@azienda.it" />
      </div>

      {/* Coordinate GPS — auto-compilate dal geocoding */}
      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-overlay)] p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-[var(--fc-foreground-muted)] uppercase tracking-wide">
            Coordinate GPS
          </p>
          <div className="flex items-center gap-2">
            {geocoding && (
              <span className="flex items-center gap-1 text-xs text-[var(--fc-foreground-muted)]">
                <Loader2 className="h-3 w-3 animate-spin" /> Ricerca...
              </span>
            )}
            {geocoded && !geocoding && (
              <span className="flex items-center gap-1 text-xs text-[var(--fc-success)]">
                <CheckCircle2 className="h-3 w-3" /> Rilevate automaticamente
              </span>
            )}
            <button
              type="button"
              onClick={() => void runGeocode()}
              disabled={geocoding}
              className="text-xs text-[var(--fc-primary)] hover:underline disabled:opacity-50"
            >
              Rileva da indirizzo
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Latitudine" value={form.lat} onChange={set('lat')} placeholder="45.4642" />
          <Input label="Longitudine" value={form.lng} onChange={set('lng')} placeholder="9.1900" />
        </div>
        <p className="text-xs text-[var(--fc-foreground-subtle)]">
          Compilate automaticamente dall&apos;indirizzo. Puoi modificarle manualmente se necessario.
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-[var(--fc-border)]">
        <Button variant="secondary" onClick={onCancel} disabled={loading}>Annulla</Button>
        <Button onClick={() => onSave(form)} disabled={loading || !form.name.trim()}>
          {loading ? 'Salvataggio...' : 'Salva filiale'}
        </Button>
      </div>
    </div>
  )
}

export function BranchesPage() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editBranch, setEditBranch] = useState<Branch | null>(null)

  const { data: branches = [], isLoading } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const res = await api.get<Branch[]>('/companies/branches')
      return res.data ?? []
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: Partial<Branch> & { lat?: string; lng?: string }) =>
      api.post('/companies/branches', data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['branches'] }); setShowForm(false) },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: Partial<Branch> & { id: string; lat?: string; lng?: string }) =>
      api.put(`/companies/branches/${id}`, data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['branches'] }); setEditBranch(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/companies/branches/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['branches'] }),
  })

  function parseLngLat(branch: Branch): { lat?: number; lng?: number } {
    try { return branch.notes ? (JSON.parse(branch.notes) as { lat?: number; lng?: number }) : {} } catch { return {} }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Filiali"
        subtitle="Sedi operative della tua azienda"
        actions={
          <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowForm(true)}>
            Nuova filiale
          </Button>
        }
      />

      {showForm && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-4">Nuova filiale</h3>
          <BranchForm
            onSave={(d) => createMutation.mutate(d)}
            onCancel={() => setShowForm(false)}
            loading={createMutation.isPending}
          />
        </Card>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : branches.length === 0 && !showForm ? (
        <EmptyState
          icon={<Building2 className="h-12 w-12" />}
          title="Nessuna filiale"
          description="Aggiungi la prima sede operativa della tua azienda."
          action={{ label: 'Aggiungi filiale', onClick: () => setShowForm(true) }}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {branches.map(branch => {
            const geo = parseLngLat(branch)
            return (
              <div key={branch.id} className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 space-y-3">
                {editBranch?.id === branch.id ? (
                  <>
                    <h3 className="text-sm font-semibold">Modifica {branch.name}</h3>
                    <BranchForm
                      initial={branch}
                      onSave={(d) => updateMutation.mutate({ id: branch.id, ...d })}
                      onCancel={() => setEditBranch(null)}
                      loading={updateMutation.isPending}
                    />
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)]">
                          <Building2 className="h-5 w-5 text-[var(--fc-primary)]" />
                        </div>
                        <div>
                          <p className="font-semibold text-[var(--fc-foreground)]">{branch.name}</p>
                          {branch.city && <p className="text-xs text-[var(--fc-foreground-muted)]">{branch.city}</p>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setEditBranch(branch)}
                          className="rounded p-1.5 text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)]"
                          title="Modifica"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Eliminare la filiale "${branch.name}"?`)) deleteMutation.mutate(branch.id)
                          }}
                          className="rounded p-1.5 text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-red-500"
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-[var(--fc-foreground-muted)]">
                      {branch.address && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span>{branch.address}{branch.postalCode ? `, ${branch.postalCode}` : ''}</span>
                        </div>
                      )}
                      {branch.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5 shrink-0" />
                          <span>{branch.phone}</span>
                        </div>
                      )}
                      {branch.email && (
                        <div className="flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          <span>{branch.email}</span>
                        </div>
                      )}
                      {geo.lat && geo.lng ? (
                        <div className="flex items-center gap-1.5 text-xs text-[var(--fc-success)]">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span>GPS: {geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs text-[var(--fc-warning)]">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span>Coordinate GPS mancanti</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
